import { useState, useRef, useEffect } from 'react';

const MATCH_TYPE_LABELS = {
    exact: 'Exact match',
    sized: 'Product Matched',
    fuzzy: 'Closest match',
    manual: 'Manually selected',
};

// Debounced typeahead search-select used for Deacom customer/ship-to/SKU overrides.
// `searchFn(query)` must resolve to an array of `{ id, primary, secondary }` candidates.
// `selected`, when set, renders a matched chip (with a confidence badge and a
// Change action) in place of the search input. `onManualEntry(rawId)`, when
// provided, lets the CSR type a Deacom ID directly - our search may not surface
// a record the CSR knows exists (e.g. imported after the last sync).
const DeacomSearchSelect = ({ searchFn, selected, onSelect, onClear, onManualEntry, placeholder, disabled }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [manualMode, setManualMode] = useState(false);
    const [manualValue, setManualValue] = useState('');
    const debounceRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (value) => {
        setQuery(value);
        setOpen(true);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value.trim().length < 2) {
            setResults([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            const candidates = await searchFn(value.trim());
            setResults(candidates);
            setLoading(false);
        }, 300);
    };

    const handleSelect = (candidate) => {
        onSelect(candidate);
        setQuery('');
        setResults([]);
        setOpen(false);
        setSearching(false);
    };

    const handleManualSave = () => {
        const trimmed = manualValue.trim();
        if (!trimmed) return;
        onManualEntry(trimmed);
        setManualMode(false);
        setManualValue('');
        setSearching(false);
    };

    const cancelManualMode = () => {
        setManualMode(false);
        setManualValue('');
    };

    if (selected && !searching) {
        return (
            <div className="deacom-match-chip">
                <div className="deacom-match-chip-info">
                    <div className="deacom-match-chip-info-badges">
                        {(selected.idLabels || (selected.idLabel ? [selected.idLabel] : [])).map(idLabel => (
                            <span className="deacom-match-badge id" key={idLabel}>{idLabel}</span>
                        ))}
                        {selected.matchType && MATCH_TYPE_LABELS[selected.matchType] &&
                            <span className={`deacom-match-badge confidence ${selected.matchType}`}>{MATCH_TYPE_LABELS[selected.matchType]}</span>
                        }
                    </div>
                    <span className="deacom-match-primary">{selected.primary}</span>
                </div>
                <button type="button" className="deacom-match-action pill" onClick={() => setSearching(true)} disabled={disabled}>Change</button>
            </div>
        );
    }

    if (manualMode) {
        return (
            <div className="deacom-search-select" ref={containerRef}>
                <div className="deacom-search-select-box">
                    <span className="deacom-match-badge none">No match</span>
                    <div className="deacom-search-select-input-row">
                        <input
                            type="text"
                            className="form-control border-0 fw-normal tif-color-white"
                            placeholder="Enter Deacom ID..."
                            value={manualValue}
                            autoFocus
                            onChange={e => setManualValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleManualSave(); }}
                        />
                        <button type="button" className="deacom-match-action" onClick={handleManualSave} disabled={!manualValue.trim()}>Save</button>
                        <button type="button" className="deacom-match-action clear" onClick={cancelManualMode}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="deacom-search-select" ref={containerRef}>
            <div className="deacom-search-select-box">
                {!selected && <span className="deacom-match-badge none">No match</span>}
                <div className="deacom-search-select-input-row">
                    <input
                        type="text"
                        className="form-control border-0 fw-normal tif-color-white"
                        placeholder={placeholder || 'Search...'}
                        value={query}
                        disabled={disabled}
                        autoFocus={searching}
                        onFocus={() => setOpen(true)}
                        onChange={e => handleChange(e.target.value)}
                    />
                    {
                        selected && searching &&
                        <button
                            type="button"
                            className="deacom-match-action"
                            onClick={() => { setSearching(false); setQuery(''); setResults([]); setOpen(false); }}
                        >
                            Cancel
                        </button>
                    }
                </div>
            </div>
            {
                onManualEntry &&
                <button type="button" className="deacom-match-action manual-entry" onClick={() => setManualMode(true)}>Enter ID manually</button>
            }
            {
                open && (loading || results.length > 0) &&
                <div className="deacom-search-select-results">
                    {
                        loading ?
                            <div className="deacom-search-select-loading">Searching...</div>
                        :
                            results.map(candidate => (
                                <button
                                    type="button"
                                    key={candidate.id}
                                    className="deacom-search-select-option"
                                    onClick={() => handleSelect(candidate)}
                                >
                                    <span className="primary">{candidate.primary}</span>
                                    {candidate.secondary && <span className="secondary">{candidate.secondary}</span>}
                                </button>
                            ))
                    }
                </div>
            }
            {
                selected &&
                <button type="button" className="deacom-match-action clear" onClick={() => { onClear(); setSearching(false); }}>
                    Clear match
                </button>
            }
        </div>
    );
};

export default DeacomSearchSelect;
